import { buildSchemaIndex, getExplorerChildFields, getFieldArgs, getQueryRootFields } from './schemaTypes';

export function skipWsAndComments(source, index) {
  let i = index;
  while (i < source.length) {
    const ch = source[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === ',') {
      i += 1;
      continue;
    }
    if (ch === '#') {
      while (i < source.length && source[i] !== '\n') i += 1;
      continue;
    }
    if (source.startsWith('"""', i)) {
      i += 3;
      while (i < source.length && !source.startsWith('"""', i)) i += 1;
      i += 3;
      continue;
    }
    break;
  }
  return i;
}

export function parseGraphQLIdentifier(source, index) {
  const i = skipWsAndComments(source, index);
  const match = /^[_A-Za-z][_0-9A-Za-z]*/.exec(source.slice(i));
  if (!match) return null;
  return { value: match[0], end: i + match[0].length };
}

export function parseGraphQLValue(source, index) {
  let i = skipWsAndComments(source, index);
  if (i >= source.length) return null;

  if (source[i] === '$') {
    const id = parseGraphQLIdentifier(source, i + 1);
    if (!id) return null;
    return { value: `$${id.value}`, end: id.end };
  }

  if (source[i] === '"') {
    let end = i + 1;
    let escaped = false;
    while (end < source.length) {
      if (escaped) {
        escaped = false;
        end += 1;
        continue;
      }
      if (source[end] === '\\') {
        escaped = true;
        end += 1;
        continue;
      }
      if (source[end] === '"') {
        return { value: source.slice(i, end + 1), end: end + 1 };
      }
      end += 1;
    }
    return null;
  }

  if (source.startsWith('"""', i)) {
    const close = source.indexOf('"""', i + 3);
    if (close === -1) return null;
    return { value: source.slice(i, close + 3), end: close + 3 };
  }

  if (source[i] === '[') {
    let depth = 0;
    let end = i;
    while (end < source.length) {
      if (source[end] === '[') depth += 1;
      else if (source[end] === ']') {
        depth -= 1;
        if (depth === 0) {
          return { value: source.slice(i, end + 1), end: end + 1 };
        }
      }
      end += 1;
    }
    return null;
  }

  if (source[i] === '{') {
    let depth = 0;
    let end = i;
    while (end < source.length) {
      if (source[end] === '{') depth += 1;
      else if (source[end] === '}') {
        depth -= 1;
        if (depth === 0) {
          return { value: source.slice(i, end + 1), end: end + 1 };
        }
      }
      end += 1;
    }
    return null;
  }

  const numberMatch = /^-?(?:\d+\.?\d*|\.\d+)/.exec(source.slice(i));
  if (numberMatch) {
    return { value: numberMatch[0], end: i + numberMatch[0].length };
  }

  const ident = parseGraphQLIdentifier(source, i);
  if (!ident) return null;
  return { value: ident.value, end: ident.end };
}

export function parseGraphQLArguments(source, index) {
  let i = skipWsAndComments(source, index);
  if (source[i] !== '(') return { args: {}, end: index };

  i += 1;
  const args = {};
  while (i < source.length) {
    i = skipWsAndComments(source, i);
    if (source[i] === ')') return { args, end: i + 1 };

    const name = parseGraphQLIdentifier(source, i);
    if (!name) return { args, end: i };
    i = skipWsAndComments(source, name.end);
    if (source[i] !== ':') return { args, end: i };

    const value = parseGraphQLValue(source, i + 1);
    if (!value) return { args, end: i };
    args[name.value] = value.value;
    i = skipWsAndComments(source, value.end);
  }

  return { args, end: i };
}

export function skipGraphQLFragment(source, index) {
  let i = skipWsAndComments(source, index);
  if (!source.startsWith('...', i)) return index;

  i += 3;
  i = skipWsAndComments(source, i);
  if (source[i] === '{') {
    const inline = parseGraphQLSelectionSet(source, i);
    return inline?.end ?? i;
  }

  const name = parseGraphQLIdentifier(source, i);
  return name?.end ?? i;
}

export function parseGraphQLSelection(source, index) {
  let i = skipWsAndComments(source, index);
  if (source.startsWith('...', i)) {
    const end = skipGraphQLFragment(source, i);
    return { name: null, args: {}, children: [], end };
  }

  if (source[i] === '{') {
    const inline = parseGraphQLSelectionSet(source, i);
    if (!inline) return null;
    return { name: null, args: {}, children: inline.selections, end: inline.end };
  }

  const first = parseGraphQLIdentifier(source, i);
  if (!first) return null;

  let fieldName = first.value;
  i = first.end;
  i = skipWsAndComments(source, i);
  if (source[i] === ':') {
    const actual = parseGraphQLIdentifier(source, i + 1);
    if (!actual) return null;
    fieldName = actual.value;
    i = actual.end;
  }

  const { args, end: argsEnd } = parseGraphQLArguments(source, i);
  i = skipWsAndComments(source, argsEnd);

  let children = [];
  if (source[i] === '{') {
    const selectionSet = parseGraphQLSelectionSet(source, i);
    if (selectionSet) {
      children = selectionSet.selections;
      i = selectionSet.end;
    }
  }

  return { name: fieldName, args, children, end: i };
}

export function parseGraphQLSelectionSet(source, index) {
  let i = skipWsAndComments(source, index);
  if (source[i] !== '{') return null;

  i += 1;
  const selections = [];
  while (i < source.length) {
    i = skipWsAndComments(source, i);
    if (source[i] === '}') return { selections, end: i + 1 };

    const selection = parseGraphQLSelection(source, i);
    if (!selection || selection.end <= i) return { selections, end: i };
    if (selection.name) selections.push(selection);
    i = selection.end;
  }

  return { selections, end: i };
}

export function extractOperationSelectionSet(query) {
  const trimmed = String(query ?? '').trim();
  if (!trimmed) return null;

  const opMatch = /^(?:query|mutation|subscription)\b/i.exec(trimmed);
  let start = 0;
  if (opMatch) {
    start = opMatch[0].length;
    start = skipWsAndComments(trimmed, start);

    const opName = parseGraphQLIdentifier(trimmed, start);
    if (opName) {
      const afterName = skipWsAndComments(trimmed, opName.end);
      if (trimmed[afterName] !== '(') {
        start = afterName;
      } else {
        start = opName.end;
      }
    }

    start = skipWsAndComments(trimmed, start);
    if (trimmed[start] === '(') {
      let depth = 0;
      let j = start;
      while (j < trimmed.length) {
        if (trimmed[j] === '(') depth += 1;
        else if (trimmed[j] === ')') {
          depth -= 1;
          if (depth === 0) {
            start = j + 1;
            break;
          }
        }
        j += 1;
      }
    }
  }

  start = skipWsAndComments(trimmed, start);
  if (trimmed[start] === '{') return parseGraphQLSelectionSet(trimmed, start);
  if (!opMatch && trimmed[0] === '{') return parseGraphQLSelectionSet(trimmed, 0);
  return null;
}

export function findFieldAtPath(path, schemaIndex, rootFields) {
  const parts = path.split('.');
  let fields = rootFields;
  let current = null;
  for (const part of parts) {
    current = fields.find((field) => field.name === part);
    if (!current) return null;
    fields = getExplorerChildFields(current, schemaIndex);
  }
  return current;
}

export function argValueForExplorer(arg, rawValue) {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed || trimmed === 'null') return '';

  if (trimmed.startsWith('"""') && trimmed.endsWith('"""')) {
    return trimmed.slice(3, -3);
  }
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/\\"/g, '"');
  }
  return trimmed;
}

export function walkParsedSelections(
  selections,
  schemaIndex,
  rootFields,
  pathPrefix,
  selectedPaths,
  argValues,
) {
  for (const selection of selections) {
    if (!selection?.name || selection.name === '__typename') continue;

    let field = null;
    if (!pathPrefix) {
      field = rootFields.find((rootField) => rootField.name === selection.name);
    } else {
      const parent = findFieldAtPath(pathPrefix, schemaIndex, rootFields);
      if (parent) {
        field = getExplorerChildFields(parent, schemaIndex).find(
          (child) => child.name === selection.name
        );
      }
    }
    if (!field) continue;

    const fieldPath = pathPrefix ? `${pathPrefix}.${selection.name}` : selection.name;
    selectedPaths.add(fieldPath);

    for (const [argName, rawValue] of Object.entries(selection.args || {})) {
      const arg = getFieldArgs(field).find((candidate) => candidate.name === argName);
      if (!arg) continue;
      const explorerValue = argValueForExplorer(arg, rawValue);
      if (explorerValue !== '') {
        argValues[`${fieldPath}.arg.${argName}`] = explorerValue;
      }
    }

    if (selection.children?.length) {
      walkParsedSelections(
        selection.children,
        schemaIndex,
        rootFields,
        fieldPath,
        selectedPaths,
        argValues,
      );
    }
  }
}

/**
 * Parse a saved GraphQL query back into schema-explorer paths and argument values.
 */
export function parseQueryToSelectedPaths(query, schema) {
  const selectedPaths = new Set();
  const argValues = {};
  const selectedArgPaths = new Set();
  if (!schema || !String(query ?? '').trim()) {
    return { selectedPaths, argValues, selectedArgPaths };
  }

  const parsed = extractOperationSelectionSet(query);
  if (!parsed?.selections?.length) {
    return { selectedPaths, argValues, selectedArgPaths };
  }

  const schemaIndex = buildSchemaIndex(schema);
  const rootFields = getQueryRootFields(schema, schemaIndex);
  walkParsedSelections(parsed.selections, schemaIndex, rootFields, '', selectedPaths, argValues);
  for (const argPath of Object.keys(argValues)) {
    selectedArgPaths.add(argPath);
  }
  return { selectedPaths, argValues, selectedArgPaths };
}
